/**
 * Transportation & Logistics Industry Configuration
 *
 * Supports: Fleet Vehicles, Aircraft Engines, Railway Cars, Marine Vessels, Containers
 */

import { IndustryConfig } from '../industryTypes'

export const transportationConfig: IndustryConfig = {
  id: 'transportation',
  name: 'Transportation & Logistics',
  description: 'Fleet management and logistics operations',
  icon: 'Truck',

  terminology: {
    asset: 'Vehicle',
    assets: 'Vehicles',
    assetPlural: 'Vehicles',
    assetType: 'Vehicle Type',
    failure: 'Breakdown',
    health: 'Condition',
    healthScore: 'Condition Score',
    healthy: 'In Service',
    degraded: 'Needs Attention',
    critical: 'Out of Service',
    alert: 'Alert',
    process: 'Transit',
    maintenance: 'Service',
    inspection: 'Inspection',
    downtime: 'Out of Service Time',
    workOrder: 'Service Order',
    prediction: 'Forecast',
    anomaly: 'Issue',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Depot',
    facilityPlural: 'Depots',
    location: 'Route',
    operator: 'Driver',
    supervisor: 'Fleet Manager',
    calibration: 'Calibration',
    compliance: 'DOT Compliance',
    criticality: 'Fleet Priority',
  },

  assetTypes: [
    {
      id: 'truck',
      name: 'Commercial Truck',
      icon: 'Truck',
      description: 'Heavy-duty commercial vehicles',
      category: 'Ground',
      defaultMetrics: ['fuel_efficiency', 'engine_hours', 'brake_wear', 'tire_condition'],
      maintenanceTypes: ['oil_change', 'brake_service', 'tire_rotation', 'dot_inspection'],
      criticalityLevels: [
        { id: 'critical', name: 'Revenue Critical', color: '#ef4444', description: 'Primary revenue vehicle', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High Utilization', color: '#f59e0b', description: 'High-use vehicle', downtimeCostMultiplier: 25 },
        { id: 'medium', name: 'Standard', color: '#3b82f6', description: 'Standard fleet vehicle', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Backup', color: '#6b7280', description: 'Backup/spare vehicle', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'aircraft_engine',
      name: 'Aircraft Engine',
      icon: 'Plane',
      description: 'Commercial aircraft engines',
      category: 'Aviation',
      defaultMetrics: ['exhaust_gas_temp', 'oil_consumption', 'vibration', 'fuel_flow'],
      maintenanceTypes: ['borescope_inspection', 'oil_analysis', 'performance_restoration', 'overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Line', color: '#ef4444', description: 'Active line engine', downtimeCostMultiplier: 500 },
        { id: 'high', name: 'Spare', color: '#f59e0b', description: 'Designated spare', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'QEC', color: '#3b82f6', description: 'Quick engine change', downtimeCostMultiplier: 50 },
        { id: 'low', name: 'Shop', color: '#6b7280', description: 'In shop for overhaul', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'locomotive',
      name: 'Locomotive',
      icon: 'TrainFront',
      description: 'Diesel and electric locomotives',
      category: 'Rail',
      defaultMetrics: ['power_output', 'fuel_efficiency', 'traction_motor_temp', 'brake_cylinder_pressure'],
      maintenanceTypes: ['daily_inspection', 'periodic_inspection', 'running_repair', 'overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Lead Unit', color: '#ef4444', description: 'Primary consist unit', downtimeCostMultiplier: 200 },
        { id: 'high', name: 'Active', color: '#f59e0b', description: 'Active service unit', downtimeCostMultiplier: 100 },
        { id: 'medium', name: 'Pool', color: '#3b82f6', description: 'Pool locomotive', downtimeCostMultiplier: 40 },
        { id: 'low', name: 'Stored', color: '#6b7280', description: 'Stored serviceable', downtimeCostMultiplier: 10 },
      ],
    },
    {
      id: 'container',
      name: 'Shipping Container',
      icon: 'Package',
      description: 'Intermodal shipping containers',
      category: 'Intermodal',
      defaultMetrics: ['structural_integrity', 'door_seal', 'floor_condition', 'corrosion_level'],
      maintenanceTypes: ['damage_repair', 'floor_replacement', 'door_repair', 'repainting'],
      criticalityLevels: [
        { id: 'critical', name: 'Reefer', color: '#ef4444', description: 'Refrigerated container', downtimeCostMultiplier: 30 },
        { id: 'high', name: 'Special', color: '#f59e0b', description: 'Special cargo container', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Standard', color: '#3b82f6', description: 'Standard dry container', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Aged', color: '#6b7280', description: 'End of life container', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'delivery_van',
      name: 'Delivery Van',
      icon: 'Car',
      description: 'Last-mile delivery vehicles',
      category: 'Ground',
      defaultMetrics: ['fuel_efficiency', 'delivery_completion', 'idle_time', 'route_efficiency'],
      maintenanceTypes: ['oil_change', 'brake_inspection', 'tire_service', 'cargo_area_repair'],
      criticalityLevels: [
        { id: 'critical', name: 'High Volume', color: '#ef4444', description: 'High-volume route', downtimeCostMultiplier: 25 },
        { id: 'high', name: 'Standard', color: '#f59e0b', description: 'Standard route', downtimeCostMultiplier: 10 },
        { id: 'medium', name: 'Backup', color: '#3b82f6', description: 'Backup vehicle', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Seasonal', color: '#6b7280', description: 'Seasonal vehicle', downtimeCostMultiplier: 2 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'fleet_efficiency',
      name: 'Fleet Efficiency',
      icon: 'Gauge',
      description: 'Fleet utilization and efficiency metrics',
      businessValue: 'Maximize fleet productivity and minimize costs',
      category: 'universal',
      metrics: [
        { id: 'utilization', name: 'Fleet Utilization', unit: '%', description: 'Vehicles in active use', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 70, critical: 55 } },
        { id: 'fuel_efficiency', name: 'Fuel Efficiency', unit: 'mpg', description: 'Miles per gallon', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 8, warning: 6, critical: 4 } },
        { id: 'cost_per_mile', name: 'Cost per Mile', unit: '$/mi', description: 'Total operating cost', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 1.5, warning: 2.0, critical: 2.5 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'delivery_performance',
      name: 'Delivery Performance',
      icon: 'Clock',
      description: 'On-time delivery and service metrics',
      businessValue: 'Meet delivery commitments and customer expectations',
      category: 'industry_specific',
      metrics: [
        { id: 'on_time_delivery', name: 'On-Time Delivery', unit: '%', description: 'Deliveries on schedule', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 90, critical: 85 } },
        { id: 'first_attempt', name: 'First Attempt Success', unit: '%', description: 'Delivered first attempt', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'stops_per_hour', name: 'Stops per Hour', unit: 'stops/hr', description: 'Delivery efficiency', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 15, warning: 12, critical: 8 } },
      ],
      thresholds: { healthy: { min: 95 }, warning: { min: 90, max: 95 }, critical: { max: 90 } },
      visualizations: ['gauge', 'line_chart', 'bar_chart', 'heatmap'],
    },
    {
      id: 'vehicle_health',
      name: 'Vehicle Health',
      icon: 'HeartPulse',
      description: 'Vehicle condition and reliability metrics',
      businessValue: 'Maintain fleet reliability and reduce breakdowns',
      category: 'universal',
      metrics: [
        { id: 'health_score', name: 'Health Score', unit: '%', description: 'Overall vehicle health', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 70, critical: 55 } },
        { id: 'breakdown_rate', name: 'Breakdown Rate', unit: 'per 1000mi', description: 'Breakdowns per distance', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 1.0, critical: 2.0 } },
        { id: 'pm_compliance', name: 'PM Compliance', unit: '%', description: 'Preventive maintenance on schedule', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 90, critical: 80 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['gauge', 'distribution', 'trend', 'comparison'],
    },
    {
      id: 'driver_safety',
      name: 'Driver Safety',
      icon: 'Shield',
      description: 'Driver behavior and safety metrics',
      businessValue: 'Reduce accidents and improve safety culture',
      category: 'industry_specific',
      metrics: [
        { id: 'safety_score', name: 'Safety Score', unit: '%', description: 'Overall safety rating', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 90, warning: 80, critical: 70 } },
        { id: 'harsh_events', name: 'Harsh Events', unit: 'per 1000mi', description: 'Hard braking/acceleration', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
        { id: 'speeding_pct', name: 'Speeding Percentage', unit: '%', description: 'Time over speed limit', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
      ],
      thresholds: { healthy: { min: 90 }, warning: { min: 80, max: 90 }, critical: { max: 80 } },
      visualizations: ['gauge', 'line_chart', 'distribution', 'comparison'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'pm_service',
      name: 'Preventive Maintenance Service',
      description: 'Scheduled preventive maintenance',
      category: 'preventive',
      defaultInterval: { value: 10000, unit: 'cycles' },
      laborRequirements: [
        { role: 'Fleet Technician', count: 1, hourlyRate: 45 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['diesel_mechanics', 'electrical', 'hvac'],
      partsCommonly: ['filters', 'fluids', 'belts', 'brakes'],
    },
    {
      id: 'dot_inspection',
      name: 'DOT Inspection',
      description: 'Department of Transportation annual inspection',
      category: 'condition_based',
      defaultInterval: { value: 12, unit: 'months' },
      laborRequirements: [
        { role: 'Certified Inspector', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 1, typical: 2, max: 4, unit: 'hours' },
      skillsRequired: ['dot_certification', 'inspection'],
      partsCommonly: ['inspection_forms', 'testing_equipment'],
    },
    {
      id: 'breakdown_repair',
      name: 'Breakdown Repair',
      description: 'Emergency roadside repair',
      category: 'corrective',
      laborRequirements: [
        { role: 'Mobile Technician', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 1, typical: 3, max: 8, unit: 'hours' },
      skillsRequired: ['troubleshooting', 'roadside_repair'],
      partsCommonly: ['emergency_parts', 'tow_service'],
    },
  ],

  riskFactors: [
    { id: 'breakdown_risk', name: 'Breakdown Risk', description: 'Risk of roadside breakdown', category: 'operational', weight: 0.30, calculationMethod: 'exponential', inputs: ['health_score', 'mileage', 'pm_status'] },
    { id: 'safety_risk', name: 'Safety Risk', description: 'Risk of safety incident', category: 'safety', weight: 0.30, calculationMethod: 'linear', inputs: ['driver_score', 'vehicle_defects'] },
    { id: 'compliance_risk', name: 'Compliance Risk', description: 'Risk of DOT violation', category: 'compliance', weight: 0.25, calculationMethod: 'threshold', inputs: ['inspection_status', 'hours_compliance'] },
    { id: 'cost_risk', name: 'Cost Risk', description: 'Risk of excessive costs', category: 'financial', weight: 0.15, calculationMethod: 'linear', inputs: ['repair_trend', 'fuel_trend'] },
  ],

  complianceStandards: [
    {
      id: 'fmcsa',
      name: 'FMCSA Regulations',
      shortName: 'FMCSA',
      description: 'Federal Motor Carrier Safety Administration',
      authority: 'DOT',
      region: ['US'],
      requirements: [
        { id: 'annual_inspection', name: 'Annual Inspection', description: 'Annual vehicle inspection', kpiId: 'inspection_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'hos_compliance', name: 'Hours of Service', description: 'Driver hours compliance', kpiId: 'hos_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'csa',
      name: 'CSA Safety Scores',
      shortName: 'CSA',
      description: 'Compliance, Safety, Accountability',
      authority: 'FMCSA',
      region: ['US'],
      requirements: [
        { id: 'basic_scores', name: 'BASIC Scores', description: 'Stay below intervention thresholds', kpiId: 'csa_score', threshold: 65, operator: 'lt', severity: 'required' },
      ],
      reportingFrequency: 'monthly',
    },
  ],

  visualization: {
    primaryColor: '#6366f1',
    secondaryColor: '#ec4899',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Vehicle Status Summary',
      context: 'asset_health',
      audience: ['manager', 'technician'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Mileage: {mileage} miles. Next {maintenance} due: {nextServiceDate}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'mileage', type: 'metric' },
        { name: 'maintenance', type: 'terminology' },
        { name: 'nextServiceDate', type: 'time' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'breakdown_alert',
      name: 'Breakdown Risk Alert',
      context: 'prediction',
      audience: ['manager', 'technician'],
      template: 'WARNING: {assetType} "{assetName}" has elevated {failure} risk. Current health score: {healthScore}%. Key concern: {concern}. Recommend: {recommendation}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'failure', type: 'terminology' },
        { name: 'healthScore', type: 'percentage' },
        { name: 'concern', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '7d',
    refreshInterval: 60,
    defaultDashboard: 'command-center',
  },

  // Professional Roles
  professionalRoles: [
    {
      id: 'fleet_engineer',
      category: 'sme' as const,
      name: 'Fleet Engineer',
      shortName: 'Fleet Eng',
      description: 'Manages vehicle fleet reliability and maintenance',
      responsibilities: ['Fleet optimization', 'Preventive maintenance', 'Cost control'],
      focusAreas: ['Vehicle uptime', 'Fuel efficiency', 'DOT compliance'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'compliance', 'action_items'] as const,
      },
      decisionAuthority: ['Maintenance scheduling', 'Vehicle replacement'],
      reportingTo: 'Fleet Director',
      icon: 'Truck',
    },
    // SME — Signal & Communications
    {
      id: 'signal_communications_engineer',
      category: 'sme' as const,
      name: 'Telematics & Signaling Engineer',
      shortName: 'TSig Eng',
      description: 'Owns telematics + signaling + comms across multimodal transportation (truck ELD / fleet telematics + rail PTC / signaling / track circuits)',
      responsibilities: [
        'ELD + telematics platform reliability (trucking fleet)',
        'Rail signaling + interlocking validation (where applicable)',
        'Positive Train Control (PTC) system oversight',
        'V2X + connectivity uptime monitoring',
        'On-vehicle communications equipment calibration',
      ],
      focusAreas: ['telematics_uptime', 'eld_compliance', 'ptc_availability', 'fail_safe_compliance', 'communication_latency'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'compliance', 'risk'] as const,
      },
      decisionAuthority: ['Signal equipment modifications', 'Interlocking changes', 'Communication system upgrades'],
      reportingTo: 'Chief Signal Engineer',
      icon: 'Radio',
    },
    // Operations — Rail Operations Manager
    {
      id: 'rail_operations_manager',
      category: 'executive' as const,
      name: 'Transportation Operations Manager',
      shortName: 'Ops Mgr',
      description: 'Oversees day-to-day multimodal transportation operations: dispatch, driver/crew scheduling, terminal management, service reliability',
      responsibilities: [
        'Dispatch and route scheduling coordination',
        'Driver / crew assignment and HOS compliance (49 CFR 395)',
        'Terminal + yard operations management',
        'Service disruption response and recovery',
        'On-time performance monitoring',
      ],
      focusAreas: ['on_time_performance', 'fleet_velocity', 'terminal_dwell', 'driver_utilization', 'service_reliability'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['business_impact', 'action_items', 'risk'] as const,
      },
      decisionAuthority: ['Train scheduling', 'Crew deployment', 'Service adjustments', 'Emergency response'],
      reportingTo: 'VP Rail Operations',
      icon: 'TrainFront',
    },
    // Technician — Track Maintenance
    {
      id: 'track_maintenance_technician',
      category: 'technician' as const,
      name: 'Vehicle / Way Maintenance Technician',
      shortName: 'Vehicle Tech',
      description: 'Performs hands-on inspection, repair, and maintenance of vehicles (truck / locomotive / van) and way infrastructure (rail / pavement)',
      responsibilities: [
        'Vehicle inspection per 49 CFR 396 (truck) or FRA rule (rail)',
        'Brake (TMC RP 627) + wheel-end (TMC RP 235) maintenance',
        'Rail / pavement defect detection and reporting (where applicable)',
        'Routine PM execution per DVIR / pre-trip checklists',
        'Tire / refrigerant / fluid replacement support',
      ],
      focusAreas: ['vehicle_condition', 'brake_stroke', 'wheel_end_health', 'tire_wear', 'way_defects'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items'] as const,
      },
      decisionAuthority: ['Track repair methods', 'Speed restriction recommendations', 'Immediate safety stops'],
      reportingTo: 'Track Supervisor',
      icon: 'Wrench',
    },
    // Analyst — Rolling Stock Engineer
    {
      id: 'rolling_stock_engineer',
      category: 'analyst' as const,
      name: 'Vehicle Reliability Engineer',
      shortName: 'VR Eng',
      description: 'Analyzes vehicle / rolling-stock performance data, fleet reliability trends, and component life-cycle patterns to optimize maintenance strategies (truck + rail + multimodal)',
      responsibilities: [
        'Fleet reliability and availability analysis',
        'Component failure trend analysis',
        'Maintenance interval optimization',
        'Warranty claim analysis and tracking',
        'New technology evaluation for fleet upgrades',
      ],
      focusAreas: ['fleet_availability', 'mtbf_trends', 'component_life', 'maintenance_cost_per_mile', 'failure_correlations'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['technical_details', 'business_impact', 'action_items'] as const,
      },
      decisionAuthority: ['Maintenance strategy changes', 'Component standardization', 'Fleet modification programs'],
      reportingTo: 'Chief Mechanical Officer',
      icon: 'BarChart3',
    },
    // Compliance — Safety Inspector
    {
      id: 'safety_inspector',
      category: 'sme' as const,
      name: 'Safety Inspector',
      shortName: 'Safety Insp',
      description: 'Conducts safety audits, regulatory inspections, and compliance assessments for rail operations per FRA, FTA, and company safety standards',
      responsibilities: [
        'FRA/FTA regulatory compliance audits',
        'Accident and incident investigation',
        'Safety Management System (SMS) oversight',
        'Hazard identification and risk assessment',
        'Corrective action tracking and verification',
      ],
      focusAreas: ['fra_compliance', 'incident_rates', 'audit_findings', 'corrective_actions', 'safety_culture_metrics'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['compliance', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['Safety stop orders', 'Compliance violation reporting', 'Corrective action requirements'],
      reportingTo: 'Chief Safety Officer',
      icon: 'Shield',
    },
  ],

  // Parts Catalog
  partsCatalog: {
    categories: [
      {
        id: 'locomotive_powertrain',
        name: 'Locomotive Powertrain',
        description: 'Traction motors, turbochargers, fuel injectors, and prime mover components for diesel and electric locomotives',
        parts: [
          {
            id: 'traction_motor_dc',
            partNumber: 'GE-752AF',
            name: 'DC Traction Motor',
            description: 'Self-ventilated DC traction motor for diesel-electric locomotives, 600V class',
            category: 'locomotive_powertrain',
            manufacturer: 'GE Transportation',
            unitCost: 28000,
            currency: 'USD',
            leadTime: { value: 30, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['armature_ground_fault', 'commutator_wear', 'bearing_failure'],
            averageLifespan: { value: 80000, unit: 'hours' },
            specifications: { voltage: '600V', power: '1050HP', type: 'DC series wound' },
          },
          {
            id: 'traction_motor_ac',
            partNumber: 'SIEMENS-1TB2624',
            name: 'AC Traction Motor',
            description: 'Asynchronous 3-phase AC traction motor for modern locomotives',
            category: 'locomotive_powertrain',
            manufacturer: 'Siemens',
            unitCost: 42000,
            currency: 'USD',
            leadTime: { value: 45, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['insulation_breakdown', 'bearing_failure', 'rotor_bar_crack'],
            averageLifespan: { value: 120000, unit: 'hours' },
            specifications: { voltage: '2200V', power: '1400kW', type: 'AC asynchronous' },
          },
          {
            id: 'locomotive_turbocharger',
            partNumber: 'ABB-TPS57',
            name: 'Locomotive Turbocharger Assembly',
            description: 'High-efficiency turbocharger for EMD/GE prime movers, axial turbine design',
            category: 'locomotive_powertrain',
            manufacturer: 'ABB Turbocharging',
            unitCost: 18500,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['turbo_bearing_failure', 'blade_erosion', 'seal_leak'],
            averageLifespan: { value: 40000, unit: 'hours' },
            specifications: { type: 'Axial turbine', application: 'EMD 710 / GE 7FDL' },
          },
          {
            id: 'fuel_injector_emd',
            partNumber: 'EMD-9563750',
            name: 'Unit Fuel Injector',
            description: 'Mechanical unit injector for EMD 645/710 series prime movers',
            category: 'locomotive_powertrain',
            manufacturer: 'Progress Rail (EMD)',
            unitCost: 1850,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['injector_tip_erosion', 'plunger_wear', 'seat_leak'],
            averageLifespan: { value: 15000, unit: 'hours' },
            specifications: { type: 'Mechanical unit injector', engine: 'EMD 710' },
          },
        ],
      },
      {
        id: 'signaling_equipment',
        name: 'Signaling & Communications',
        description: 'Signal relays, LED signal heads, track circuits, and communication equipment for railway signaling systems',
        parts: [
          {
            id: 'signal_relay_q',
            partNumber: 'ANSALDO-QRA1',
            name: 'Vital Signal Relay (Q-style)',
            description: 'Fail-safe vital relay for interlocking and signal circuits, plug-in type',
            category: 'signaling_equipment',
            manufacturer: 'Ansaldo STS',
            unitCost: 320,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['contact_wear', 'coil_open', 'spring_fatigue'],
            averageLifespan: { value: 20, unit: 'years' },
            specifications: { type: 'Vital Q-style', contacts: '4 front / 4 back', coilVoltage: '12V DC' },
          },
          {
            id: 'led_signal_head',
            partNumber: 'DIALIGHT-430',
            name: 'LED Signal Head Unit',
            description: 'Tri-color LED signal unit for wayside signals, replacing incandescent roundels',
            category: 'signaling_equipment',
            manufacturer: 'Dialight',
            unitCost: 850,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['led_degradation', 'driver_board_failure', 'moisture_ingress'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { colors: 'Red/Yellow/Green', visibility: '1500m', power: '15W' },
          },
          {
            id: 'track_circuit_module',
            partNumber: 'ALSTOM-TI21',
            name: 'Audio-Frequency Track Circuit Module',
            description: 'AF track circuit transmitter/receiver for train detection and broken rail detection',
            category: 'signaling_equipment',
            manufacturer: 'Alstom',
            unitCost: 4200,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['tuning_drift', 'receiver_sensitivity_loss', 'insulated_joint_failure'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { type: 'Audio-frequency jointless', frequency: '1700-2600 Hz' },
          },
        ],
      },
      {
        id: 'track_switch_components',
        name: 'Track Switch & Turnout Components',
        description: 'Switch machines, point blades, lock rods, and frog assemblies for railway track switches',
        parts: [
          {
            id: 'switch_machine_electric',
            partNumber: 'ALSTOM-CLAMPLOCK',
            name: 'Electric Switch Machine',
            description: 'Electric point machine with internal locking for mainline turnouts',
            category: 'track_switch_components',
            manufacturer: 'Alstom',
            unitCost: 12500,
            currency: 'USD',
            leadTime: { value: 28, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['motor_burnout', 'clutch_slip', 'lock_rod_misadjust', 'circuit_controller_failure'],
            averageLifespan: { value: 25, unit: 'years' },
            specifications: { type: 'Electric with clamp lock', throwForce: '6000 lbf', voltage: '110V DC' },
          },
          {
            id: 'point_blade_set',
            partNumber: 'VOESTALPINE-PB136',
            name: 'Switch Point Blade Set',
            description: 'Manganese steel point blade set for #20 turnout, heat-treated tips',
            category: 'track_switch_components',
            manufacturer: 'voestalpine',
            unitCost: 8500,
            currency: 'USD',
            leadTime: { value: 42, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['tip_wear', 'battered_heel', 'cracking'],
            averageLifespan: { value: 200, unit: 'cycles' },
            specifications: { rail: '136 lb RE', material: 'Manganese steel', turnout: '#20' },
          },
          {
            id: 'lock_rod_assembly',
            partNumber: 'NORTRAK-LR24',
            name: 'Switch Lock Rod Assembly',
            description: 'Adjustable lock rod with clevis fittings for switch point locking',
            category: 'track_switch_components',
            manufacturer: 'Nortrak',
            unitCost: 650,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['rod_bending', 'clevis_wear', 'adjustment_drift'],
            averageLifespan: { value: 15, unit: 'years' },
            specifications: { material: 'Alloy steel', adjustability: '±2 inches' },
          },
        ],
      },
      {
        id: 'rolling_stock_running_gear',
        name: 'Rolling Stock Running Gear',
        description: 'Bearings, brake shoes, wheel sets, and truck components for railcars and locomotives',
        parts: [
          {
            id: 'roller_bearing_assembly',
            partNumber: 'TIMKEN-AP2TM',
            name: 'Tapered Roller Bearing Assembly (Class K)',
            description: 'AAR Class K cartridge bearing assembly for freight car journals, 6.5x12 inch',
            category: 'rolling_stock_running_gear',
            manufacturer: 'Timken',
            unitCost: 1450,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive', 'container'],
            failureModes: ['bearing_overheating', 'cone_spalling', 'seal_failure', 'contamination'],
            averageLifespan: { value: 600000, unit: 'cycles' },
            specifications: { class: 'AAR Class K', journal: '6.5x12 inch', loadRating: '286K gross' },
          },
          {
            id: 'composite_brake_shoe',
            partNumber: 'WABTEC-COBRA',
            name: 'High-Friction Composite Brake Shoe',
            description: 'AAR-approved composite brake shoe for freight and passenger service',
            category: 'rolling_stock_running_gear',
            manufacturer: 'Wabtec',
            unitCost: 35,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['locomotive', 'container'],
            failureModes: ['shoe_wear', 'thermal_cracking', 'shoe_loss'],
            averageLifespan: { value: 60000, unit: 'cycles' },
            specifications: { type: 'High-friction composite', approval: 'AAR S-401' },
          },
          {
            id: 'wheel_set_36in',
            partNumber: 'AMSTED-36C',
            name: 'Wheel Set (36-inch, Class C)',
            description: 'AAR Class C forged steel wheel set, 36-inch diameter, for heavy-haul freight service',
            category: 'rolling_stock_running_gear',
            manufacturer: 'Amsted Rail',
            unitCost: 4200,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['locomotive', 'container'],
            failureModes: ['flat_spots', 'flange_wear', 'tread_shelling', 'thermal_cracking'],
            averageLifespan: { value: 500000, unit: 'cycles' },
            specifications: { diameter: '36 inch', class: 'AAR Class C', material: 'Forged carbon steel' },
          },
        ],
      },
      {
        id: 'electrification_components',
        name: 'Electrification & Catenary',
        description: 'Catenary insulators, tension weights, pantograph components, and overhead contact wire for electrified rail operations',
        parts: [
          {
            id: 'catenary_insulator',
            partNumber: 'LAPP-CPI25',
            name: 'Composite Post Insulator (25kV)',
            description: 'Silicone rubber composite insulator for 25kV AC catenary support',
            category: 'electrification_components',
            manufacturer: 'Lapp Insulators',
            unitCost: 280,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['tracking_damage', 'flashover', 'mechanical_fracture', 'pollution_buildup'],
            averageLifespan: { value: 30, unit: 'years' },
            specifications: { voltage: '25kV AC', material: 'Silicone rubber / FRP', creepage: '800mm' },
          },
          {
            id: 'tension_weight_stack',
            partNumber: 'PFIST-TW450',
            name: 'Catenary Tension Weight Stack',
            description: 'Cast iron counterweight stack for automatic catenary wire tensioning',
            category: 'electrification_components',
            manufacturer: 'Pfisterer',
            unitCost: 950,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' as const },
            criticalityLevel: 'standard' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['pulley_seizure', 'cable_fraying', 'travel_limit_exceeded'],
            averageLifespan: { value: 40, unit: 'years' },
            specifications: { weight: '450 kg per stack', material: 'Cast iron' },
          },
          {
            id: 'pantograph_carbon_strip',
            partNumber: 'SCHUNK-08-3106',
            name: 'Pantograph Carbon Contact Strip',
            description: 'Copper-impregnated carbon contact strip for pantograph collector head',
            category: 'electrification_components',
            manufacturer: 'Schunk',
            unitCost: 185,
            currency: 'USD',
            leadTime: { value: 7, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['locomotive'],
            failureModes: ['strip_wear', 'arc_damage', 'chipping'],
            averageLifespan: { value: 20000, unit: 'cycles' },
            specifications: { material: 'Copper-impregnated carbon', length: '1450mm' },
          },
        ],
      },
    ],
  },

  // Root Cause Library
  rootCauseLibrary: {
    categories: [
      // -----------------------------------------------------------------------
      // 1. Signaling System Failures
      // -----------------------------------------------------------------------
      {
        id: 'signaling_failures',
        name: 'Signaling System Failures',
        description: 'Root causes related to railway signaling, interlocking, and train control systems',
        icon: 'Radio',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'track_circuit_failure',
            name: 'Track Circuit Failure',
            description: 'Loss of train detection capability due to track circuit malfunction, insulated joint failure, or ballast fouling reducing impedance',
            category: 'signaling_failures',
            symptoms: [
              'False occupancy indication (right-side failure)',
              'Failure to detect train (wrong-side failure)',
              'Intermittent signal drops',
              'Track circuit receiver alarm',
              'Reduced shunting sensitivity',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure track circuit voltage at receiver end',
                expectedResult: 'Voltage within calibrated operating range (typically 2-6V AC)',
                tools: ['Track circuit test meter', 'Multimeter'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Inspect insulated joints for damage or bridging',
                expectedResult: 'Clean separation with no metallic bridging or moisture paths',
                tools: ['Inspection mirror', 'Insulation tester'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Measure ballast resistance between rails',
                expectedResult: 'Ballast resistance above minimum threshold (>2 ohms per 1000ft)',
                tools: ['Ballast resistance meter'],
                duration: 25,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 4,
                action: 'Verify tuning of AF track circuit transmitter and receiver',
                expectedResult: 'Frequencies matched and signal levels within specification',
                tools: ['Signal analyzer', 'Oscilloscope'],
                duration: 45,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Schedule regular insulated joint inspections',
              'Monitor ballast condition in wet areas',
              'Implement track circuit trending and alarming',
              'Replace aging analog circuits with digital systems',
              'Perform seasonal impedance testing',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['track_circuits', 'insulated_joints', 'relay_interfaces', 'receivers'],
            requiredExpertise: ['sme' as const, 'technician' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 12, unit: 'hours' as const },
            costRange: { min: 500, max: 8000, currency: 'USD' },
            industryReferences: ['AREMA Signal Manual', 'FRA Part 236'],
          },
          {
            id: 'relay_failure',
            name: 'Vital Relay Failure',
            description: 'Failure of safety-critical vital relay due to contact contamination, spring fatigue, or coil degradation',
            category: 'signaling_failures',
            symptoms: [
              'Signal displaying incorrect aspect',
              'Switch failing to lock',
              'Interlocking logic error',
              'Relay buzzing or chattering',
              'Intermittent circuit behavior',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Remove and visually inspect relay contacts',
                expectedResult: 'Contacts clean, no pitting or contamination',
                tools: ['Relay test stand', 'Contact burnisher'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Measure coil resistance and pickup/dropout voltages',
                expectedResult: 'Values within manufacturer specifications',
                tools: ['Relay test set', 'Multimeter'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Test contact resistance and dielectric strength',
                expectedResult: 'Contact resistance <0.1 ohm, insulation per specification',
                tools: ['Micro-ohmmeter', 'Hi-pot tester'],
                duration: 25,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Implement relay testing and replacement program',
              'Maintain controlled environment in relay houses',
              'Track relay age and service cycles',
              'Keep spare relay stock at critical locations',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['vital_relays', 'relay_racks', 'interlocking_circuits'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 0.5, typical: 2, max: 6, unit: 'hours' as const },
            costRange: { min: 300, max: 2500, currency: 'USD' },
            industryReferences: ['AREMA C&S Manual Ch. 11', 'FRA Part 236.110'],
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 2. Mechanical Wear (Rolling Stock)
      // -----------------------------------------------------------------------
      {
        id: 'mechanical_wear',
        name: 'Mechanical Wear (Rolling Stock)',
        description: 'Root causes related to mechanical wear and degradation of locomotive and railcar components',
        icon: 'Cog',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'traction_motor_failure',
            name: 'Traction Motor Failure',
            description: 'Degradation or failure of locomotive traction motors due to armature ground fault, commutator wear, or bearing failure',
            category: 'mechanical_wear',
            symptoms: [
              'Reduced tractive effort',
              'Ground relay tripping',
              'Smoke or burning smell from truck',
              'Elevated motor temperature alarm',
              'Flashover at commutator',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform megger test on motor windings to ground',
                expectedResult: 'Insulation resistance >1 MΩ (ideally >10 MΩ)',
                tools: ['Megohmmeter (500V)'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Inspect commutator through inspection covers',
                expectedResult: 'Smooth surface, no flat spots or copper drag',
                tools: ['Flashlight', 'Inspection mirror'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Check brush condition and spring tension',
                expectedResult: 'Brushes within wear limits, springs providing proper pressure',
                tools: ['Brush spring gauge', 'Calipers'],
                duration: 25,
                skillLevel: 'basic' as const,
              },
              {
                order: 4,
                action: 'Measure bearing clearance and listen for abnormal noise',
                expectedResult: 'Bearing play within specification, no grinding or rumbling',
                tools: ['Dial indicator', 'Stethoscope'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Implement traction motor current trending',
              'Schedule regular brush inspections and replacements',
              'Monitor motor temperature differentials across fleet',
              'Perform periodic megger testing during servicing',
              'Follow proper loading protocols to avoid thermal stress',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['traction_motors', 'commutators', 'brushes', 'motor_bearings'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 4, typical: 12, max: 48, unit: 'hours' as const },
            costRange: { min: 5000, max: 45000, currency: 'USD' },
            industryReferences: ['AAR M-927', 'EMD Maintenance Manual'],
          },
          {
            id: 'wheel_defects',
            name: 'Wheel Defects',
            description: 'Wheel tread damage including flat spots, shelling, spalling, and flange wear from braking, track conditions, or metallurgical issues',
            category: 'mechanical_wear',
            symptoms: [
              'Rhythmic impact noise (flat spots)',
              'Wheel impact load detector (WILD) alerts',
              'Excessive vibration at speed',
              'Flange wear alarms from wayside detectors',
              'Visual tread surface damage',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure wheel tread profile with wheel profile gauge',
                expectedResult: 'Profile within AAR condemning limits for flange height, thickness, and tread hollow',
                tools: ['Wheel profile gauge', 'Vernier caliper'],
                duration: 20,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Inspect tread surface for flat spots and shelling',
                expectedResult: 'No flat spots >2.5 inches or shelling exceeding limits',
                tools: ['Flashlight', 'Straight edge', 'Pit gauge'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Review wayside detector data for impact trends',
                expectedResult: 'Impact forces within acceptable thresholds',
                tools: ['WILD data system'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Monitor wayside wheel impact detector data',
              'Implement wheel re-profiling schedule',
              'Optimize braking parameters to reduce flat spots',
              'Track wheel life data by manufacturer and lot',
              'Use lubrication on curves to reduce flange wear',
            ],
            typicalProbability: 0.20,
            affectedComponents: ['wheels', 'axles', 'bearings', 'rail'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 2, typical: 6, max: 24, unit: 'hours' as const },
            costRange: { min: 1000, max: 12000, currency: 'USD' },
            industryReferences: ['AAR MSRP Rule 41', 'AAR S-660'],
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 3. Track Infrastructure Degradation
      // -----------------------------------------------------------------------
      {
        id: 'track_degradation',
        name: 'Track Infrastructure Degradation',
        description: 'Root causes related to deterioration of track geometry, rail, ties, and ballast',
        icon: 'Route',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'track_geometry_defects',
            name: 'Track Geometry Defects',
            description: 'Deviation of track alignment, profile, cross-level, or gauge beyond FRA limits due to subgrade settlement, ballast fouling, or thermal effects',
            category: 'track_degradation',
            symptoms: [
              'Rough ride quality at specific locations',
              'Track geometry car exception reports',
              'Speed restriction required',
              'Visible track misalignment',
              'Tie plate cutting into ties',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Run track geometry measurement (geometry car or portable device)',
                expectedResult: 'All parameters within FRA Class limits for line speed',
                tools: ['Track geometry car', 'Portable geometry system'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Inspect subgrade drainage and ballast condition',
                expectedResult: 'Good drainage, ballast free of fouling material',
                tools: ['Visual inspection', 'Ballast sampling tools'],
                duration: 45,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Measure gauge and cross-level at defect locations',
                expectedResult: 'Gauge 56.0-57.5 inches, cross-level within tolerance',
                tools: ['Track gauge', 'Cross-level board'],
                duration: 20,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Schedule regular geometry car inspections',
              'Implement tamping and surfacing program based on degradation rates',
              'Improve drainage at problem locations',
              'Replace fouled ballast in chronic areas',
              'Monitor seasonal effects on subgrade',
            ],
            typicalProbability: 0.22,
            affectedComponents: ['track_geometry', 'ballast', 'subgrade', 'ties'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 4, typical: 12, max: 72, unit: 'hours' as const },
            costRange: { min: 2000, max: 50000, currency: 'USD' },
            industryReferences: ['FRA Track Safety Standards Part 213', 'AREMA Manual for Railway Engineering Ch. 5'],
          },
          {
            id: 'rail_defects',
            name: 'Rail Defects',
            description: 'Internal or surface rail defects including detail fractures, transverse fissures, head-web separation, and corrugation',
            category: 'track_degradation',
            symptoms: [
              'Rail flaw detector findings',
              'Visible surface defects (shelling, head checks)',
              'Broken rail alarms from track circuits',
              'Corrugation noise at speed',
              'Joint bar cracking',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform ultrasonic rail flaw detection',
                expectedResult: 'No internal defects exceeding FRA action thresholds',
                tools: ['Ultrasonic rail flaw detector'],
                duration: 120,
                skillLevel: 'advanced' as const,
              },
              {
                order: 2,
                action: 'Visual inspection of rail surface for shelling, head checks, and spalling',
                expectedResult: 'Surface within acceptable wear limits',
                tools: ['Flashlight', 'Rail profile gauge'],
                duration: 30,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Measure rail head wear profile',
                expectedResult: 'Head wear and gauge face wear within condemning limits',
                tools: ['Rail profile gauge', 'Rail head wear template'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Regular ultrasonic rail flaw detection program',
              'Rail grinding to remove surface defects and restore profile',
              'Rail lubrication on curves to reduce gauge face wear',
              'Track rail age and tonnage for replacement planning',
              'Monitor broken rail detection system availability',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['rail', 'joints', 'welds', 'tie_plates'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 48, unit: 'hours' as const },
            costRange: { min: 1000, max: 30000, currency: 'USD' },
            industryReferences: ['FRA Part 213 Subpart F', 'AREMA Ch. 4 Rail'],
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 4. Electrical / Power Distribution Faults
      // -----------------------------------------------------------------------
      {
        id: 'electrical_power',
        name: 'Electrical / Power Distribution Faults',
        description: 'Root causes related to traction power, catenary systems, and on-board electrical distribution',
        icon: 'Zap',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'catenary_fault',
            name: 'Catenary / OCS Fault',
            description: 'Overhead contact system faults including wire breaks, insulator failures, pantograph dewirement, and tension loss',
            category: 'electrical_power',
            symptoms: [
              'Loss of traction power to train',
              'Pantograph damage or carbon strip wear',
              'Circuit breaker trips at substation',
              'Arc flash reports from train crews',
              'Catenary wire sag between supports',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection of catenary from hi-rail vehicle or tower car',
                expectedResult: 'Wire height, stagger, and tension within specification',
                tools: ['Tower car', 'Measurement pole', 'Binoculars'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Measure contact wire height and stagger',
                expectedResult: 'Height 15-23 ft, stagger within ±12 inches of track center',
                tools: ['Laser measurement tool', 'Stagger gauge'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Inspect insulators for tracking, flashover damage, or contamination',
                expectedResult: 'Insulators clean, no evidence of tracking or mechanical damage',
                tools: ['Binoculars', 'IR camera'],
                duration: 45,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Regular catenary geometry measurement runs',
              'Insulator washing program in polluted areas',
              'Pantograph wear monitoring and strip replacement',
              'Tension weight travel limit checks',
              'Thermal imaging surveys for hot connections',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['contact_wire', 'messenger_wire', 'insulators', 'pantographs', 'tension_devices'],
            requiredExpertise: ['sme' as const, 'technician' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 1000, max: 25000, currency: 'USD' },
            industryReferences: ['AREMA Manual Ch. 33', 'EN 50119'],
          },
          {
            id: 'locomotive_electrical_fault',
            name: 'Locomotive Electrical System Fault',
            description: 'On-board electrical faults including main generator failure, auxiliary power unit issues, and control system malfunctions',
            category: 'electrical_power',
            symptoms: [
              'Engine load test failure',
              'Auxiliary power loss (lighting, HVAC)',
              'Computer fault codes on display',
              'Ground relay tripping repeatedly',
              'Erratic throttle response',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Download and review fault codes from locomotive computer',
                expectedResult: 'Identify specific fault type and affected system',
                tools: ['Laptop with diagnostic software', 'Download cable'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Perform load test to verify power output at each notch',
                expectedResult: 'Horsepower and amperage within specification at all throttle positions',
                tools: ['Load test facility', 'Recording instruments'],
                duration: 60,
                skillLevel: 'advanced' as const,
              },
              {
                order: 3,
                action: 'Check main generator and companion alternator insulation',
                expectedResult: 'Insulation resistance >1 MΩ for each winding',
                tools: ['Megohmmeter'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Regular load testing during scheduled inspections',
              'Insulation resistance trending on generators and alternators',
              'Keep locomotive software updated',
              'Clean and inspect electrical cabinets during PM',
              'Monitor auxiliary system performance data',
            ],
            typicalProbability: 0.14,
            affectedComponents: ['main_generator', 'companion_alternator', 'auxiliary_generator', 'control_system', 'contactors'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 36, unit: 'hours' as const },
            costRange: { min: 500, max: 35000, currency: 'USD' },
            industryReferences: ['EMD Maintenance Instruction MI-EP', 'GE Transportation Service Manual'],
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 5. Environmental & Weather Damage
      // -----------------------------------------------------------------------
      {
        id: 'environmental_weather',
        name: 'Environmental & Weather Damage',
        description: 'Root causes related to weather events, seasonal effects, and environmental hazards affecting rail operations',
        icon: 'CloudRain',
        color: '#10b981',
        rootCauses: [
          {
            id: 'thermal_rail_stress',
            name: 'Thermal Rail Stress',
            description: 'Rail buckling (sun kinks) in extreme heat or pull-apart fractures in extreme cold caused by constrained thermal expansion of CWR',
            category: 'environmental_weather',
            symptoms: [
              'Visible lateral track shift (buckle)',
              'Rail gap at joint or weld (pull-apart)',
              'Slow order required for temperature',
              'Track circuit failure from broken rail',
              'Audible rail creaking or clicking',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure rail temperature and compare to neutral temperature',
                expectedResult: 'Rail stress within allowable range for current temperature differential',
                tools: ['Rail thermometer', 'Rail stress monitor'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Walk track in affected area checking for lateral displacement',
                expectedResult: 'No lateral shift exceeding 2 inches from design alignment',
                tools: ['String line', 'Tape measure'],
                duration: 30,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Verify rail anchoring and adequate ballast shoulder',
                expectedResult: 'Anchors tight, 12-inch minimum ballast shoulder on both sides',
                tools: ['Anchor applicator', 'Measuring stick'],
                duration: 45,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Establish heat slow-order protocols based on rail temperature',
              'Monitor rail neutral temperature and adjust when needed',
              'Ensure adequate ballast shoulder and crib depth',
              'Maintain proper rail anchoring patterns',
              'Identify and monitor buckle-prone locations',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['rail', 'ballast', 'anchors', 'joints', 'welds'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 500, max: 20000, currency: 'USD' },
            industryReferences: ['FRA Part 213.119', 'AREMA Ch. 5'],
          },
          {
            id: 'flooding_washout',
            name: 'Flooding & Washout',
            description: 'Track subgrade erosion, culvert blockage, and ballast washout from heavy precipitation or snowmelt',
            category: 'environmental_weather',
            symptoms: [
              'Track settlement or misalignment at drainage crossings',
              'Water pooling around ballast section',
              'Muddy ballast and pumping ties',
              'Culvert overflow or blockage',
              'Slope failure near right-of-way',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Walk and inspect drainage structures and culverts',
                expectedResult: 'Culverts clear, adequate flow capacity, no erosion at headwalls',
                tools: ['Visual inspection', 'Measuring rod'],
                duration: 60,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Check track geometry at drainage crossings',
                expectedResult: 'No excessive settlement or alignment deviation',
                tools: ['Track geometry tools', 'Level'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Annual culvert and drainage inspection program',
              'Implement early warning flood monitoring sensors',
              'Clear vegetation and debris from drainage paths',
              'Reinforce subgrade at known problem locations',
              'Establish flood patrol procedures',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['subgrade', 'ballast', 'culverts', 'ties', 'track_geometry'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 8, typical: 24, max: 168, unit: 'hours' as const },
            costRange: { min: 5000, max: 200000, currency: 'USD' },
            industryReferences: ['AREMA Ch. 1 Roadway and Ballast', 'FRA Emergency Order protocols'],
          },
        ],
      },
      // -----------------------------------------------------------------------
      // 6. Communication System Failures
      // -----------------------------------------------------------------------
      {
        id: 'communication_failures',
        name: 'Communication System Failures',
        description: 'Root causes related to railway radio, data communication, PTC data links, and dispatch communication systems',
        icon: 'Wifi',
        color: '#ec4899',
        rootCauses: [
          {
            id: 'ptc_communication_loss',
            name: 'PTC Communication Loss',
            description: 'Loss of Positive Train Control data link between locomotive, wayside, and back office due to radio coverage gaps, antenna failures, or network congestion',
            category: 'communication_failures',
            symptoms: [
              'PTC system entering fallback/restricted mode',
              'Loss of track authority updates on display',
              'Communication timeout alarms',
              'Train forced to reduced speed operations',
              'Data link retry counters escalating',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check PTC onboard radio signal strength and link status',
                expectedResult: 'Signal strength above minimum threshold at current location',
                tools: ['PTC diagnostic display', 'Signal strength meter'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Verify antenna connections and condition on locomotive',
                expectedResult: 'Antenna properly mounted, cables intact, no corrosion at connectors',
                tools: ['Visual inspection', 'Cable tester'],
                duration: 20,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Review base station coverage map and outage reports',
                expectedResult: 'Confirm whether loss is localized coverage gap or systemic issue',
                tools: ['Network management system', 'Coverage maps'],
                duration: 30,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Conduct regular RF coverage surveys along corridors',
              'Monitor PTC communication success rates by segment',
              'Implement redundant communication paths (cellular backup)',
              'Schedule antenna inspection during locomotive servicing',
              'Maintain radio base station preventive maintenance program',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['ptc_radio', 'antennas', 'base_stations', 'back_office_server', 'onboard_computer'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 24, unit: 'hours' as const },
            costRange: { min: 200, max: 15000, currency: 'USD' },
            industryReferences: ['FRA Part 236 Subpart I (PTC)', '49 CFR 236.1005'],
          },
          {
            id: 'dispatch_radio_failure',
            name: 'Dispatch Radio System Failure',
            description: 'Failure of railroad radio communication between dispatch centers and train crews due to repeater outages, frequency interference, or equipment malfunction',
            category: 'communication_failures',
            symptoms: [
              'Inability to contact train crews',
              'Garbled or weak radio transmissions',
              'Dead zones on operating territory',
              'Cross-talk or interference from adjacent channels',
              'Emergency radio channel unresponsive',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Test radio on multiple channels from affected location',
                expectedResult: 'Determine if issue is frequency-specific or location-specific',
                tools: ['Portable radio', 'Frequency counter'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Check repeater site status via remote monitoring',
                expectedResult: 'Repeater online, transmit power normal, antenna VSWR acceptable',
                tools: ['Network monitoring system'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Inspect locomotive radio equipment and antenna system',
                expectedResult: 'Radio functional, antenna connections secure, VSWR <2:1',
                tools: ['VSWR meter', 'Wattmeter', 'Cable tester'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Maintain repeater site backup power (batteries and generators)',
              'Regular radio equipment testing during locomotive servicing',
              'Monitor repeater site performance remotely',
              'Conduct periodic coverage testing with test train',
              'Keep FCC licensing current and manage frequency coordination',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['locomotive_radios', 'repeater_stations', 'antennas', 'dispatch_consoles'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 12, unit: 'hours' as const },
            costRange: { min: 200, max: 8000, currency: 'USD' },
            industryReferences: ['FCC Part 90', 'AAR S-9152'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'deferred_maintenance_cascade',
        name: 'Deferred Maintenance Cascade',
        description: 'Progressive failure chain when scheduled maintenance is deferred on critical rail infrastructure',
        sequence: ['Maintenance deferred due to budget or access', 'Component degradation accelerates', 'Speed restrictions imposed', 'Adjacent components overstressed', 'Service-affecting failure'],
        indicators: ['Growing maintenance backlog', 'Increasing slow orders', 'Rising unplanned failure rate'],
        preventionStrategy: 'Maintain PM schedule compliance above 90% with priority scoring for safety-critical systems',
      },
      {
        id: 'seasonal_signal_failures',
        name: 'Seasonal Signal System Failures',
        description: 'Pattern of signal system failures correlated with seasonal weather changes affecting track circuits and outdoor equipment',
        sequence: ['Heavy rain or snow melt', 'Ballast resistance drops', 'Track circuit reliability degrades', 'False occupancy events increase', 'Dispatching delays cascade'],
        indicators: ['Seasonal spike in signal trouble calls', 'Ballast resistance below threshold', 'Increased false proceeds or false restrictive events'],
        preventionStrategy: 'Pre-season ballast testing and drainage improvement at vulnerable locations',
      },
      {
        id: 'wheel_rail_interaction',
        name: 'Wheel-Rail Interaction Degradation',
        description: 'Coupled degradation of wheel profiles and rail profiles from poor contact geometry',
        sequence: ['Wheel profiles deviate from design', 'Contact patch shifts on rail head', 'Increased lateral forces on curves', 'Rail gauge face wear accelerates', 'Wheel flange wear accelerates'],
        indicators: ['Rising wheel condemnation rates', 'Increased rail replacement on curves', 'Derailment risk elevation'],
        preventionStrategy: 'Coordinated wheel re-profiling and rail grinding programs with consistent profile targets',
      },
    ],
  },

  // Labor Rates
  laborRates: {
    roles: [
      { roleId: 'diesel_tech', name: 'Diesel Technician', hourlyRate: 45, currency: 'USD', certifications: ['ASE Master'] },
      { roleId: 'fleet_mechanic', name: 'Fleet Mechanic', hourlyRate: 40, currency: 'USD', certifications: ['ASE Certified'] },
      { roleId: 'fleet_engineer', name: 'Fleet Engineer', hourlyRate: 75, currency: 'USD', certifications: ['PE', 'Fleet Management'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
